import { supabase, isCloudMode } from '../supabase.ts';
import { authStore } from './auth.svelte.ts';

export interface WorkspaceMember {
  user_id:      string;
  workspace_id: string;
  role:         'owner' | 'admin' | 'member' | 'viewer';
  joined_at:    string;
  user: {
    id:           string;
    email:        string;
    display_name: string | null;
    avatar_url:   string | null;
  };
}

export interface WorkspaceInvitation {
  id:           string;
  workspace_id: string;
  email:        string;
  role:         string;
  token:        string;
  invited_by:   string;
  expires_at:   string;
  accepted_at:  string | null;
  created_at:   string;
}

export const membersStore = $state({
  members:     [] as WorkspaceMember[],
  invitations: [] as WorkspaceInvitation[],
  loading:     false,
});

export async function loadMembers(workspaceId: string) {
  if (!isCloudMode || !supabase) return;
  membersStore.loading = true;
  try {
    const { data, error } = await supabase
      .from('workspace_members')
      .select(`
        user_id, workspace_id, role, joined_at,
        user:users(id, email, display_name, avatar_url)
      `)
      .eq('workspace_id', workspaceId);
    if (error) throw error;
    membersStore.members = (data ?? []) as unknown as WorkspaceMember[];

    const { data: invData } = await supabase
      .from('workspace_invitations')
      .select('*')
      .eq('workspace_id', workspaceId)
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString());
    membersStore.invitations = invData ?? [];
  } finally {
    membersStore.loading = false;
  }
}

export async function inviteMember(workspaceId: string, email: string, role: string): Promise<string | null> {
  if (!isCloudMode || !supabase || !authStore.user) return 'Not authenticated';
  const { error } = await supabase
    .from('workspace_invitations')
    .insert({
      workspace_id: workspaceId,
      email:        email.toLowerCase().trim(),
      role,
      invited_by:   authStore.user.id,
    });
  if (error) return error.message;
  await loadMembers(workspaceId);
  return null;
}

export async function removeMember(workspaceId: string, userId: string): Promise<string | null> {
  if (!isCloudMode || !supabase) return 'Not in cloud mode';
  const { error } = await supabase
    .from('workspace_members')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId);
  if (error) return error.message;
  membersStore.members = membersStore.members.filter(m => m.user_id !== userId);
  return null;
}

export async function updateMemberRole(workspaceId: string, userId: string, role: string): Promise<string | null> {
  if (!isCloudMode || !supabase) return 'Not in cloud mode';
  const { error } = await supabase
    .from('workspace_members')
    .update({ role })
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId);
  if (error) return error.message;
  membersStore.members = membersStore.members.map(m =>
    m.user_id === userId ? { ...m, role: role as WorkspaceMember['role'] } : m
  );
  return null;
}

export async function revokeInvitation(invitationId: string, workspaceId: string): Promise<void> {
  if (!isCloudMode || !supabase) return;
  await supabase.from('workspace_invitations').delete().eq('id', invitationId);
  membersStore.invitations = membersStore.invitations.filter(i => i.id !== invitationId);
}

// Accept an invitation by token (called on the invite acceptance page)
export async function acceptInvitation(token: string): Promise<{ workspaceId: string | null; error: string | null }> {
  if (!isCloudMode || !supabase || !authStore.user) return { workspaceId: null, error: 'Not authenticated' };

  const { data: invite, error: fetchErr } = await supabase
    .from('workspace_invitations')
    .select('*')
    .eq('token', token)
    .single();
  if (fetchErr || !invite) return { workspaceId: null, error: 'Invitation not found or expired' };

  const { error: memberErr } = await supabase
    .from('workspace_members')
    .insert({ workspace_id: invite.workspace_id, user_id: authStore.user.id, role: invite.role });
  if (memberErr && !memberErr.message.includes('duplicate')) return { workspaceId: null, error: memberErr.message };

  await supabase.from('workspace_invitations').update({ accepted_at: new Date().toISOString() }).eq('id', invite.id);
  return { workspaceId: invite.workspace_id, error: null };
}
